// CreateMemberAttractionGenders.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberAttractionGenders.module.scss";
import { ATTRACTION_GENDERS } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q8) */
import mrImg from "@/assets/UI/memberCreate/q8Mr.webp";
import mrsImg from "@/assets/UI/memberCreate/q8Mrs.webp";
import mrMImg from "@/assets/UI/memberCreate/q8MrM.webp";
import mrsMImg from "@/assets/UI/memberCreate/q8MrsM.webp";

/* ---- Framer animations (same feel as CreateMemberGender) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 0.72, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 0.58, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Simple emoji meta per option */
const ATTRACTION_META = {
  men: "🧑‍🦱",
  women: "👩‍🦰",
  non_binary_trans: "🌈",
  couples: "💞",
  any_vibe: "✨",
};

/* Closing text key helper – personalized by member’s gender */
function getClosingKey(rawGender) {
  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.attraction_genders.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.attraction_genders.closing.woman";
  }
  return "onboarding.attraction_genders.closing.other";
}

function toggleChoice(current, value) {
  if (current.includes(value)) return current.filter((v) => v !== value);
  return [...current, value];
}

export default function CreateMemberAttractionGenders() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoices, setMemberChoices] = useState([]);
  const [partnerChoices, setPartnerChoices] = useState([]);

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.attraction_genders.title", {
    defaultValue: "Attraction",
  });

  const intro1 = t("onboarding.attraction_genders.intro1", {
    defaultValue: "Tell us what kinds of people pull you in.",
  });
  const intro2 = t("onboarding.attraction_genders.intro2", {
    defaultValue: "You can pick more than one.",
  });
  const intro3 = t("onboarding.attraction_genders.intro3", {
    defaultValue: "This helps us show you better matches.",
  });

  const singleQuestion = t("onboarding.attraction_genders.single.question", {
    defaultValue: "Who are you most attracted to?",
  });

  const coupleLead = t("onboarding.attraction_genders.couple.lead", {
    defaultValue: "Pick for you, and pick for your partner too.",
  });
  const coupleDetail = t("onboarding.attraction_genders.couple.detail", {
    defaultValue:
      "This keeps recommendations clean and respectful for both of you.",
  });

  const partnerLabel = t("onboarding.attraction_genders.partner.label", {
    defaultValue: "Now pick your partner’s attraction mix",
  });

  const baseOptions =
    Array.isArray(ATTRACTION_GENDERS) && ATTRACTION_GENDERS.length
      ? ATTRACTION_GENDERS
      : ["men", "women", "non_binary_trans", "couples", "any_vibe"];

  const options = baseOptions.map((value) => ({
    value,
    emoji: ATTRACTION_META[value] || "🌙",
    title: t(`onboarding.attraction_genders.options.${value}.label`, {
      defaultValue: value,
    }),
    hint: t(`onboarding.attraction_genders.tapToSelect.${value}`, {
      defaultValue: "Tap to add this vibe to your attraction mix",
    }),
  }));

  const hasMember = memberChoices.length > 0;
  const hasPartner = partnerChoices.length > 0;
  const closingKey =
    hasMember && (!isCouple || hasPartner) ? getClosingKey(rawGender) : null;
  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  const guideTitle = t("onboarding.attraction_genders.guide.title", {
    defaultValue: "Quick note",
  });
  const guideBody = t("onboarding.attraction_genders.guide.body", {
    defaultValue:
      "This doesn’t lock you in. It just helps your feed feel more like your vibe.",
  });

  const submitDisabled =
    submitting || !memberChoices.length || (isCouple && !partnerChoices.length);

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoices.length) {
      toast.info(
        t("onboarding.attraction_genders.toast.member_required", {
          defaultValue: "Pick at least one for you.",
        }),
      );
      return;
    }
    if (isCouple && !partnerChoices.length) {
      toast.info(
        t("onboarding.attraction_genders.toast.partner_required", {
          defaultValue: "Pick at least one for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberAttractionGenders: memberChoices,
          partnerAttractionGenders: partnerChoices,
        }
      : {
          memberAttractionGenders: memberChoices,
        };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/attraction_genders",
        payload,
        { skipToast: true },
      );

      await refreshMe();
      navigate(data?.next || "/member/connection_types", { replace: true });
    } catch (err) {
      console.error("update attraction genders failed:", err);
      toast.error(
        t("onboarding.attraction_genders.toast.save_error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function renderOptionsBlock({ name, subtitleText, choices, setChoices }) {
    return (
      <div className={cls.block}>
        {subtitleText ? (
          <div className={cls.blockTitle}>{subtitleText}</div>
        ) : null}

        <fieldset className={cls.options}>
          <legend className="sr-only">{subtitleText || title}</legend>

          {options.map((opt) => {
            const checked = choices.includes(opt.value);

            return (
              <label
                key={`${name}-${opt.value}`}
                className={`${cls.option} ${checked ? cls.optionActive : ""}`}
              >
                <input
                  type="checkbox"
                  name={name}
                  value={opt.value}
                  checked={checked}
                  onChange={() =>
                    setChoices((prev) => toggleChoice(prev, opt.value))
                  }
                />

                <span className={cls.optionBody}>
                  <span className={cls.optionEmoji} aria-hidden="true">
                    {opt.emoji}
                  </span>

                  <span className={cls.optionText}>
                    <strong>{opt.title}</strong>
                    <small>{opt.hint}</small>
                  </span>

                  <span className={cls.optionDot} aria-hidden="true" />
                </span>
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  }

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>

              <div className={cls.formSub}>{intro1}</div>
              <div className={cls.formSub}>{intro2}</div>
              <div className={cls.formSub}>{intro3}</div>

              {!isCouple ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {singleQuestion}
                </div>
              ) : (
                <>
                  <div className={cls.formSub} style={{ marginTop: 8 }}>
                    {coupleLead}
                  </div>
                  <div className={cls.formSub}>{coupleDetail}</div>
                </>
              )}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {renderOptionsBlock({
              name: "memberAttractionGenders",
              subtitleText: singleQuestion,
              choices: memberChoices,
              setChoices: setMemberChoices,
            })}

            {isCouple &&
              renderOptionsBlock({
                name: "partnerAttractionGenders",
                subtitleText: partnerLabel,
                choices: partnerChoices,
                setChoices: setPartnerChoices,
              })}

            <div className={cls.readNote}>
              <strong>💡 {guideTitle}</strong>
              <p>{guideBody}</p>
            </div>

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
